import { useCallback, useState } from "react";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useScreenAlert } from "@hooks/use-screen-alert";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import type { SmAiConversation } from "@hooks/use-sm-ai-chat/types";

/**
 * Lists SM AI conversation threads for the current user/site via
 * `GET /wp-json/smassets/v1/ai/conversations` (backend addition needed
 * alongside the existing POST/GET-by-id routes in `AiController.php`).
 */
export const useSmAiConversations = () => {
  const { setError } = useScreenAlert();
  const { user } = useUserContext();
  const { refreshUserToken } = useRefreshTokenMutation();
  const { getApiUrl } = useMultiSiteContext();

  const [conversations, setConversations] = useState<SmAiConversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [available, setAvailable] = useState(true);

  const authedFetch = useCallback(
    (url: string, init: RequestInit = {}) =>
      fetchWithTokenRefresh(
        url,
        {
          ...init,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.authToken ?? ""}`,
            ...(init.headers ?? {}),
          },
        },
        () => refreshUserToken(user?.refreshToken ?? "")
      ),
    [user?.authToken, user?.refreshToken, refreshUserToken]
  );

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await authedFetch(getApiUrl("wp-json/smassets/v1/ai/conversations"));
      if (response.status === 404) {
        setAvailable(false);
        setConversations([]);
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data: SmAiConversation[] = await response.json();
      setConversations(data);
    } catch (err) {
      setError("Error loading SM AI conversations: " + (err as Error)?.message);
    } finally {
      setIsLoading(false);
    }
  }, [authedFetch, getApiUrl, setError]);

  return {
    conversations,
    isLoading,
    available,
    refresh,
  };
};
